import {
  Breakpoint,
  ButtonSize,
  ButtonTheme,
  FontWeight,
  QuizAnswer,
  QuizAnswerSize,
  QuizzAnswerIconType,
  SimpleMarkdown,
  Typography,
  TypographyVariant,
} from '@elearning-platform/ui';
import React from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { QuizFeedback } from '../../interfaces';

import {
  FeedbackContainer,
  ResultContentContainer,
  ResultImage,
  ResultImageContainer,
  ResultQuestionsContainer,
  ResultWrapper,
  StyledButton,
} from './quizResultScreen.styled';
import { QuizResultScreenProps } from './quizResultScreen.types';

export const QuizResultScreen: React.FC<QuizResultScreenProps> = (props: QuizResultScreenProps): JSX.Element => {
  const {
    image,
    isPassed,
    isAttemptsLimitReached,
    isSubmitted,
    isFinalResult,
    feedback,
    onButtonClick,
    headline,
    supportingText,
    correctAnswers,
  }: QuizResultScreenProps = props;
  const { t }: TransProps<never> = useTranslation();
  const isSmallDesktop: boolean = useMediaQuery({
    query: Breakpoint.SmallDesktop,
  });

  return (
    <ResultWrapper>
      <ResultContentContainer>
        <Typography fontWeight={FontWeight.Bold} variant={isSmallDesktop ? TypographyVariant.DisplaySM : TypographyVariant.TextXL}>
          {headline}
        </Typography>
        {!!isSubmitted && (
          isFinalResult ? (
            <ResultQuestionsContainer>
              {feedback?.map((question: QuizFeedback): JSX.Element => (
                <QuizAnswer
                  additionalInfo={question.additionalInfo}
                  answer={question.correctAnswers}
                  content={question.question}
                  iconType={question.isCorrect ? QuizzAnswerIconType.Correct : QuizzAnswerIconType.Incorrect}
                  key={question.question}
                  size={question.additionalInfo ? QuizAnswerSize.Medium : QuizAnswerSize.Small}
                />
              ))}
            </ResultQuestionsContainer>
          ) : (
            <FeedbackContainer>
              <Typography variant={isSmallDesktop ? TypographyVariant.DisplayXS : TypographyVariant.TextXL}>
                <SimpleMarkdown text={t(supportingText)} />
              </Typography>
              {
                (isAttemptsLimitReached && !isPassed) ? (
                  <>
                    <ResultQuestionsContainer>
                      {correctAnswers?.map((answer: string): JSX.Element => (
                        <QuizAnswer
                          content={answer}
                          iconType={QuizzAnswerIconType.Correct}
                          key={answer}
                        />
                      ))}
                    </ResultQuestionsContainer>
                    {!!onButtonClick && (
                      <StyledButton
                        fullWidth={!isSmallDesktop}
                        label={t('competence.app.scrollLesson.componentsV2.quizResultScreen.nextQuestion')}
                        onClick={onButtonClick}
                        size={ButtonSize.Large}
                        variant={ButtonTheme.PrimaryBrand}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {!isSmallDesktop && (
                      <ResultImageContainer>
                        <ResultImage src={image} />
                      </ResultImageContainer>
                    )}
                    {!!isPassed && (
                      <ResultQuestionsContainer>
                        {correctAnswers?.map((answer: string): JSX.Element => (
                          <QuizAnswer
                            content={answer}
                            iconType={QuizzAnswerIconType.Correct}
                            key={answer}
                          />
                        ))}
                      </ResultQuestionsContainer>
                    )}
                    {!!onButtonClick && (
                      <StyledButton
                        fullWidth={!isSmallDesktop}
                        label={isPassed
                          ? t('competence.app.scrollLesson.componentsV2.quizResultScreen.nextQuestion')
                          : t('competence.app.scrollLesson.componentsV2.quizResultScreen.tryAgain')
                        }
                        onClick={onButtonClick}
                        size={ButtonSize.Large}
                        variant={ButtonTheme.PrimaryBrand}
                      />
                    )}
                  </>
                )
              }
            </FeedbackContainer>
          ))}
      </ResultContentContainer>
      {!!isSmallDesktop && (
        <ResultImageContainer>
          <ResultImage src={image} />
        </ResultImageContainer>
      )}
    </ResultWrapper>
  );
};
